(function () {
  'use strict';

  /**
   * Multi-variation product cards (Bont).
   *
   * Products tagged `multi-card` render one card per colourway. The server sends a
   * single real product-card block plus one empty placeholder per remaining
   * colourway; this script clones the real card into each placeholder and
   * specialises every card in the group.
   *
   * The block is deliberately rendered once server side — printing captured block
   * output more than once duplicates shopify_attributes and breaks Theme Editor
   * block selection.
   */

  /**
   * Returns the product URL for a variant, preserving the locale root and any
   * existing query parameters.
   * @param {string} baseUrl
   * @param {string} variantId
   * @returns {string}
   */
  function buildVariantUrl(baseUrl, variantId) {
    if (!baseUrl || !variantId) return baseUrl || '';

    var placeholderOrigin = 'https://multi-card.invalid';
    var url;

    try {
      url = new URL(baseUrl, placeholderOrigin);
    } catch (error) {
      return baseUrl;
    }

    url.searchParams.set('variant', variantId);

    var href = url.href;
    if (href.indexOf(placeholderOrigin) === 0) {
      return href.slice(placeholderOrigin.length);
    }

    return href;
  }

  /**
   * True when a value is a Shopify resource id — digits only, at least one.
   *
   * Ids from the markup are interpolated into attribute selectors, so anything
   * that is not a plain id is rejected rather than escaped.
   * @param {*} value
   * @returns {boolean}
   */
  function isResourceId(value) {
    if (typeof value !== 'string' || value.length === 0) return false;

    for (var i = 0; i < value.length; i++) {
      var code = value.charCodeAt(i);
      if (code < 48 || code > 57) return false;
    }

    return true;
  }

  /**
   * Chooses which media id a card should show.
   *
   * The server-emitted id on the card's wrapper wins. It is resolved from the option
   * value itself, so it exists whether or not the product's colour option carries
   * swatches — `blocks/swatches.liquid` renders nothing at all without them, and the
   * swatch input is where the id used to come from. The swatch's own id stays as the
   * fallback, so a card rendered by an older section still picks up an image.
   * @param {?string} wrapperMediaId
   * @param {?string} swatchMediaId
   * @returns {?string}
   */
  function preferredMediaId(wrapperMediaId, swatchMediaId) {
    if (typeof wrapperMediaId === 'string' && wrapperMediaId.length > 0) return wrapperMediaId;
    if (typeof swatchMediaId === 'string' && swatchMediaId.length > 0) return swatchMediaId;
    return null;
  }

  /**
   * Where a media id sits in the slide list `initial-slide` indexes into.
   *
   * The list is every slide in DOM order, hidden ones included — see `gallerySlides()`
   * for why that is the collection that counts. Slides with no `slide-id` still take up
   * a position, so their null id is passed through rather than skipped.
   * @param {Array<?string>} slideIds - each slide's `slide-id`, in DOM order
   * @param {string} mediaId
   * @returns {number} the index, or -1 when the media is not in this gallery
   */
  function mediaSlideIndex(slideIds, mediaId) {
    if (!slideIds || typeof mediaId !== 'string' || mediaId.length === 0) return -1;

    for (var i = 0; i < slideIds.length; i++) {
      if (slideIds[i] === mediaId) return i;
    }

    return -1;
  }

  if (typeof document !== 'undefined') {
    var HYDRATED = 'data-multi-card-hydrated';
    var PLACEHOLDER = 'data-multi-card-placeholder';
    var PRODUCT_ID = 'data-multi-card-product-id';
    var VARIANT_ID = 'data-multi-card-variant-id';
    var MEDIA_ID = 'data-multi-card-media-id';

    // A group of cards for one product always lives inside one grid or one
    // resource list. Sibling adjacency does not hold: the carousel, editorial and
    // bento layouts wrap every list item in its own <slideshow-slide> or cell, so
    // a placeholder's real card is never its previous sibling there.
    var GROUP_SCOPE = '.product-grid, .resource-list, .shopify-section';

    // The containers that can ever receive placeholders. Everything else on the
    // site — cart, account, blog, the product page itself — is left unwatched.
    var WATCHED_CONTAINERS = '.product-grid, .resource-list';

    /**
     * Finds the fragment markup for one variant inside a card.
     * @param {Element} card
     * @param {string} kind - 'title' or 'price'
     * @param {string} variantId
     * @returns {string|null}
     */
    function fragmentHtml(card, kind, variantId) {
      var template = card.querySelector('template[data-multi-card-fragments="' + kind + '"]');
      if (!template) return null;

      var fragment = template.content.querySelector('[data-variant-id="' + variantId + '"]');
      return fragment ? fragment.innerHTML : null;
    }

    /**
     * Removes the Theme Editor's block metadata from a clone. Only the server-rendered
     * original may carry it — the editor assumes one DOM node per block instance.
     * @param {Element} clone
     */
    function stripEditorAttributes(clone) {
      var nodes = clone.querySelectorAll('[data-shopify-editor-block]');
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].removeAttribute('data-shopify-editor-block');
      }
      clone.removeAttribute('data-shopify-editor-block');
    }

    /**
     * Makes every `id` on a clone unique to its colourway.
     *
     * `snippets/product-card.liquid` gives the card and its overlay link an id each,
     * and `cloneNode(true)` copies both verbatim. `assets/product-card.js` writes the
     * link's id into the URL hash for scroll restoration, and `sections/main-collection.liquid`
     * looks it up with `document.getElementById` — with duplicates that resolves to
     * the first match, restoring to the wrong card. Suffixing with the variant id
     * keeps the ids unique and stable across renders.
     *
     * Clones only. The server-rendered original keeps the ids Liquid gave it. Every
     * clone is taken from that original, never from another clone, so an id is
     * suffixed exactly once.
     * @param {Element} clone
     * @param {string} variantId
     */
    function suffixIds(clone, variantId) {
      var suffix = '-' + variantId;

      if (clone.hasAttribute('id')) {
        clone.setAttribute('id', clone.getAttribute('id') + suffix);
      }

      var nodes = clone.querySelectorAll('[id]');
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].setAttribute('id', nodes[i].getAttribute('id') + suffix);
      }
    }

    /**
     * The slides that `initial-slide` indexes into.
     *
     * `Slideshow.initialSlide` is `refs.slides[initialSlideIndex]`, and `refs.slides`
     * is every `[ref="slides[]"]` element whose nearest component ancestor is this
     * slideshow — hidden slides included, because the ref collection never filters on
     * `hidden`. Counting a different set silently lands on the wrong slide: `[slide-id]`
     * alone would drop the id-less placeholder slide `snippets/card-gallery.liquid`
     * renders for a blank product, and a nested slideshow's slides belong to that
     * slideshow, not this one.
     * @param {Element} slideshow
     * @returns {Element[]}
     */
    function gallerySlides(slideshow) {
      var candidates = slideshow.querySelectorAll('[ref="slides[]"]');
      var slides = [];

      for (var i = 0; i < candidates.length; i++) {
        var parent = candidates[i].parentNode;
        if (parent && parent.closest && parent.closest('slideshow-component') !== slideshow) continue;
        slides.push(candidates[i]);
      }

      return slides;
    }

    /**
     * Puts one colourway's image on a card.
     *
     * Mirrors `ProductCard.#updateVariantImages()`: hide the other colourways'
     * slides first, then ask the slideshow to select. Both halves matter.
     *
     * There is a third part, `initial-slide`. `Slideshow` reads that attribute during
     * its own setup and, a scheduled frame later, selects the slide it names — so a
     * `select()` we made synchronously beforehand is simply overwritten and every card
     * settles back on slide 0. Setting the attribute to the target's index turns that
     * deferred call from a competitor into the thing that does the work: whether the
     * element initialises after we touch it or has already initialised — in which case
     * `attributeChangedCallback` re-selects on a microtask — both paths land on the
     * same slide.
     *
     * `Slideshow.select()` returns early when its scroll state was never built, and
     * that state is only built when the gallery had more than one unhidden slide at
     * connect. `snippets/card-gallery.liquid` hides every variant image after the
     * first, so a product whose media are all attached to variants — the very shape
     * this feature exists for — connects with a single unhidden slide and every
     * later `select()` is a no-op. The hide pass is what actually puts the right
     * image on screen in that case; `select()` positions the strip when it can.
     *
     * Hiding the other colourways also stops a shopper swiping the "Black" card's
     * gallery through to the red image.
     * @param {Element} slideshow
     * @param {string} mediaId
     */
    function showColourwayMedia(slideshow, mediaId) {
      var slides = gallerySlides(slideshow);
      var slideIds = [];
      var i;

      for (i = 0; i < slides.length; i++) {
        slideIds.push(slides[i].getAttribute('slide-id'));
      }

      var index = mediaSlideIndex(slideIds, mediaId);

      // The media may not be in this gallery at all — a combined listing's child
      // product carries its own featured media, and a variant featured with a video
      // or a 3D model is never marked `variant-image`. Hiding on a miss would hide
      // every colourway's slide and unhide none, leaving the card with no image at
      // all. The server-rendered state is always the better fallback.
      if (index === -1) return;

      var target = slides[index];

      for (i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('variant-image') === null) continue;
        slides[i].hidden = slides[i].getAttribute('slide-id') !== mediaId;
      }

      slideshow.setAttribute('initial-slide', String(index));

      if (typeof slideshow.select === 'function') {
        slideshow.select({ id: mediaId }, undefined, { animate: false });
      }

      // Defensive: whatever select() did or did not do, the intended slide is the
      // one that must be visible.
      if (target.hidden) target.hidden = false;
    }

    /**
     * Points a card's gallery at its colourway.
     *
     * `settled` says whether the card is already in the document. A clone is not, and
     * must not be — `initial-slide` is read once, during the slideshow's setup, so the
     * attribute has to be on the element before it connects. That also spares the
     * shopper the flash of the wrong image.
     *
     * The server-rendered original is already connected and may already have
     * initialised, so the attribute alone cannot be trusted to be read again. The
     * immediate call puts the right image up as early as possible; the nested
     * `requestAnimationFrame` re-applies it after the slideshow's own scheduled frame
     * has been and gone.
     * @param {Element} card
     * @param {string} variantId
     * @param {?Element} wrapper - the card's wrapper, which carries the media id
     * @param {boolean} settled - true when the card is already in the document
     */
    function applyColourwayMedia(card, variantId, wrapper, settled) {
      // The colourway's image, chosen from the id the section emitted on this card's
      // wrapper — the same server-side resolution that produces the title and price
      // fragments — so it no longer depends on a swatch row existing.
      //
      // The wrapper is passed in rather than found with `closest()`: this runs on a
      // clone before it is inserted, so there are no ancestors to search.
      var swatch = card.querySelector('input[data-variant-id="' + variantId + '"]');
      var mediaId = preferredMediaId(
        wrapper ? wrapper.getAttribute(MEDIA_ID) : null,
        swatch ? swatch.getAttribute('data-option-media-id') : null
      );
      var slideshow = card.querySelector('[ref="slideshow"]');
      if (!mediaId || !slideshow) return;

      showColourwayMedia(slideshow, mediaId);

      if (!settled || typeof window.requestAnimationFrame !== 'function') return;

      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          showColourwayMedia(slideshow, mediaId);
        });
      });
    }

    /**
     * Rewrites one card to stand for one colourway.
     *
     * The gallery is not part of this. `applyColourwayMedia()` handles it, because it
     * has to run at a different moment for a clone than for the original.
     * @param {Element} card
     * @param {string} variantId
     */
    function specialise(card, variantId) {
      var link = card.querySelector('[ref="productCardLink"]');
      var productUrl = link ? link.getAttribute('href') : '';
      var variantUrl = buildVariantUrl(productUrl, variantId);

      var refs = ['productCardLink', 'cardGalleryLink', 'productTitleLink'];
      for (var i = 0; i < refs.length; i++) {
        var target = card.querySelector('[ref="' + refs[i] + '"]');
        if (target) target.setAttribute('href', variantUrl);
      }

      var titleHtml = fragmentHtml(card, 'title', variantId);
      var titleHost = card.querySelector('[ref="productTitleLink"]');
      if (titleHtml && titleHost) titleHost.innerHTML = titleHtml;

      // Target the inner span, never <product-price> itself — that element also holds
      // the tax note and instalments markup.
      var priceHtml = fragmentHtml(card, 'price', variantId);
      var priceHost = card.querySelector('product-price .product__price');
      if (priceHtml && priceHost) priceHost.innerHTML = priceHtml;

      // Checking the swatch makes VariantPicker.selectedOption resolve to this
      // colourway, so the existing pointer-leave handler restores the right image.
      // The resolver emits `option_value.variant.id` and the swatch input carries the
      // same id in `data-variant-id` — see snippets/variant-swatches.liquid, which
      // resets its first-available fallback per option value so the two agree.
      //
      // Optional, and no longer where the media id comes from: a product whose colour
      // option has no swatches configured renders no swatch row at all, because
      // blocks/swatches.liquid gates the whole block on `product_has_swatches`.
      var swatch = card.querySelector('input[data-variant-id="' + variantId + '"]');
      if (swatch) {
        var checked = card.querySelectorAll('input[data-variant-id]:checked');
        for (var j = 0; j < checked.length; j++) {
          checked[j].checked = false;
        }
        swatch.checked = true;
      }

      // A split card stands for one colourway, so its swatches navigate rather than
      // swapping in place — swapping would leave two cards showing the same colour.
      // Capture phase, so this runs before the variant picker's own handler.
      card.addEventListener(
        'click',
        function (event) {
          var label = event.target.closest ? event.target.closest('.variant-option__swatch label') : null;
          if (!label) return;

          var input = label.querySelector('input[data-variant-id]');
          if (!input) return;

          event.preventDefault();
          event.stopPropagation();

          var target = buildVariantUrl(productUrl, input.getAttribute('data-variant-id'));
          if (target) window.location.href = target;
        },
        true
      );
    }

    /**
     * The grid, resource list or section a group of cards shares.
     * @param {Element} element
     * @returns {ParentNode}
     */
    function groupScope(element) {
      if (!element.closest) return document;
      return element.closest(GROUP_SCOPE) || document;
    }

    /**
     * Finds the server-rendered wrapper a placeholder should clone from.
     *
     * Matching is by product id, and placeholders are excluded, so the source is
     * always the pristine original: never another placeholder that this pass has
     * already filled, and never a neighbouring product whose own card rendered
     * empty. The mobile duplicate of a grid list is a separate `.resource-list`,
     * so each copy resolves within itself.
     * @param {Element} placeholder
     * @param {string} productId
     * @returns {Element|null}
     */
    function findSourceWrapper(placeholder, productId) {
      if (!isResourceId(productId)) return null;

      var scope = groupScope(placeholder);
      var wrappers = scope.querySelectorAll('[' + PRODUCT_ID + '="' + productId + '"]:not([' + PLACEHOLDER + '])');

      for (var i = 0; i < wrappers.length; i++) {
        if (wrappers[i].querySelector('product-card')) return wrappers[i];
      }

      return null;
    }

    /**
     * Clones the real card into every placeholder and specialises the whole group.
     * Idempotent — already-hydrated wrappers are skipped, so it is safe to re-run
     * after infinite scroll, filtering and Theme Editor re-renders.
     * @param {ParentNode} [root]
     */
    function hydrate(root) {
      var scope = root && root.querySelectorAll ? root : document;
      var placeholders = scope.querySelectorAll('[' + PLACEHOLDER + ']:not([' + HYDRATED + '])');
      if (!placeholders.length) return;

      for (var i = 0; i < placeholders.length; i++) {
        var placeholder = placeholders[i];
        var variantId = placeholder.getAttribute(VARIANT_ID);
        var productId = placeholder.getAttribute(PRODUCT_ID);
        // Both ids reach an attribute selector — productId in findSourceWrapper(),
        // variantId in fragmentHtml() and specialise() — so both are held to the
        // same guard.
        if (!isResourceId(variantId) || !isResourceId(productId)) continue;

        var source = findSourceWrapper(placeholder, productId);
        if (!source) continue;

        var original = source.querySelector('product-card');

        // Specialise the original once. Its natural variant is the product's first
        // available one, which is not reliably the first colourway.
        if (!source.hasAttribute(HYDRATED)) {
          var sourceVariantId = source.getAttribute(VARIANT_ID);
          if (isResourceId(sourceVariantId)) {
            specialise(original, sourceVariantId);
            applyColourwayMedia(original, sourceVariantId, source, true);
          }
          source.setAttribute(HYDRATED, 'true');
        }

        var clone = original.cloneNode(true);
        stripEditorAttributes(clone);
        suffixIds(clone, variantId);
        // Before insertion, and only before: the slideshow reads `initial-slide` once,
        // as it connects.
        applyColourwayMedia(clone, variantId, placeholder, false);
        placeholder.appendChild(clone);
        specialise(clone, variantId);
        placeholder.setAttribute(HYDRATED, 'true');
      }
    }

    /**
     * True when the node is, or contains, an unhydrated-or-not placeholder wrapper.
     * @param {Node} node
     * @returns {boolean}
     */
    function holdsPlaceholder(node) {
      if (!node || node.nodeType !== 1) return false;
      if (node.hasAttribute(PLACEHOLDER)) return true;
      return !!(node.querySelector && node.querySelector('[' + PLACEHOLDER + ']'));
    }

    var observer = null;
    var hydrationQueued = false;

    /**
     * @param {MutationRecord[]} mutations
     */
    function onMutations(mutations) {
      // A burst of appended nodes only needs one pass; hydrate() is idempotent
      // thanks to the HYDRATED guard, so a coalesced pass loses nothing.
      if (hydrationQueued) return;

      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n++) {
          if (!holdsPlaceholder(added[n])) continue;

          hydrationQueued = true;
          window.setTimeout(function () {
            hydrate(document);
            // Cleared after the pass, which does not coalesce hydration's own
            // mutations away: observer records are delivered at the microtask
            // checkpoint at the end of the task, after the flag has already been
            // cleared. They are harmless because the clones hydration appends hold
            // no placeholders, so holdsPlaceholder() rejects every one and no second
            // pass is queued. What the flag does buy is a single pass for a burst of
            // nodes appended within one batch of records.
            hydrationQueued = false;
          }, 0);
          return;
        }
      }
    }

    /**
     * Watches one element for placeholders arriving later.
     *
     * Re-observing an element that is already observed with the same options
     * replaces its registration rather than adding a second one, so this is safe
     * to call repeatedly.
     * @param {Node} element
     */
    function watch(element) {
      if (typeof MutationObserver === 'undefined') return;
      if (!element || element.nodeType !== 1) return;

      if (!observer) observer = new MutationObserver(onMutations);
      observer.observe(element, { childList: true, subtree: true });
    }

    /**
     * Watches the grids and resource lists on this page.
     *
     * Filtering morphs the whole section, replacing the grid inside it, so the
     * section wrapper is watched in preference to the grid — it is the node that
     * survives the morph. Pages with no grid and no resource list — cart, account,
     * blog, the product page — install no observer at all.
     */
    function watchContainers() {
      var containers = document.querySelectorAll(WATCHED_CONTAINERS);

      for (var i = 0; i < containers.length; i++) {
        watch(containers[i].closest('.shopify-section') || containers[i]);
      }
    }

    function init() {
      hydrate(document);
      watchContainers();
    }

    if (document.readyState !== 'loading') {
      init();
    } else {
      document.addEventListener('DOMContentLoaded', init);
    }

    // Theme Editor section re-render.
    document.addEventListener('shopify:section:load', init);

    // `filter:update` fires when the shopper changes a filter, before the replacement
    // markup has been fetched, so there is nothing to hydrate yet. What it is good
    // for is making sure the section about to be morphed is being watched — a
    // collection currently showing "no products found" has no grid to watch, so
    // without this the results that filtering brings back would never hydrate.
    document.addEventListener('filter:update', function (event) {
      var origin = event.target;
      if (origin && origin.nodeType === 1 && origin.closest) {
        watch(origin.closest('.shopify-section'));
      }
      watchContainers();
    });
  }

  // CommonJS export shim (for node --test; harmless in browser).
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      buildVariantUrl: buildVariantUrl,
      isResourceId: isResourceId,
      preferredMediaId: preferredMediaId,
      mediaSlideIndex: mediaSlideIndex
    };
  }
})();
